import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./BugFixing.css";

export default function BugFixing() {
    const bugTypes = [
        { title: "Gameplay Bugs", text: "Fix broken rules, scoring errors, stuck rounds, level progression issues, reward mistakes, and win/loss state problems.", icon: "fa-solid fa-gamepad" },
        { title: "Multiplayer Bugs", text: "Resolve matchmaking failures, room sync issues, reconnect problems, turn mismatches, player state errors, and result conflicts.", icon: "fa-solid fa-users-rays" },
        { title: "Wallet & Economy Bugs", text: "Fix coin balance errors, reward claims, purchase failures, refund states, bonus logic, offer rules, and transaction inconsistencies.", icon: "fa-solid fa-coins" },
        { title: "UI & Flow Bugs", text: "Repair broken buttons, popups, loading states, navigation problems, clipping, layout issues, onboarding, and screen state defects.", icon: "fa-solid fa-object-group" },
        { title: "LiveOps Bugs", text: "Fix events, missions, leaderboards, notifications, offers, tournaments, segmentation, and campaign activation problems.", icon: "fa-solid fa-bolt" },
        { title: "Build & Release Bugs", text: "Resolve crashes, device-specific defects, store build issues, permissions, login failures, version conflicts, and release blockers.", icon: "fa-solid fa-screwdriver-wrench" }
    ];

    const triage = [
        { id: "BUG-241", issue: "Player reward claim duplicates after reconnect", area: "Wallet", severity: "Critical", owner: "Backend" },
        { id: "BUG-218", issue: "Private room invite fails on second attempt", area: "Multiplayer", severity: "High", owner: "Game Server" },
        { id: "BUG-197", issue: "Leaderboard rank does not refresh after match", area: "LiveOps", severity: "High", owner: "API" },
        { id: "BUG-163", issue: "Shop popup overlaps on small Android screens", area: "UI", severity: "Medium", owner: "Frontend" },
        { id: "BUG-142", issue: "Result screen shows old score after rematch", area: "Gameplay", severity: "Medium", owner: "Client" }
    ];

    const lanes = [
        { title: "Report", count: "12", items: ["Crash on loading", "Reward popup loop", "Offer not visible"] },
        { title: "Triage", count: "08", items: ["Wallet mismatch", "Room reconnect", "Rank delay"] },
        { title: "Fix", count: "05", items: ["Timer sync", "Store claim", "Guest login"] },
        { title: "Regression", count: "09", items: ["Match result", "Bonus flow", "Tournament entry"] }
    ];

    const fixFlow = [
        { step: "01", title: "Reproduce", text: "Confirm bug steps, affected device, account state, build version, server data, and expected behavior." },
        { step: "02", title: "Diagnose", text: "Trace client logs, backend responses, realtime events, wallet records, UI states, and edge cases." },
        { step: "03", title: "Fix", text: "Patch the client, API, game server, admin panel, database rule, configuration, or content issue." },
        { step: "04", title: "Verify", text: "Retest the original bug, adjacent flows, related player states, and impacted platforms." },
        { step: "05", title: "Prevent", text: "Add regression notes, validation rules, monitoring checks, and release safeguards where useful." }
    ];

    const coverage = [
        "Unity Game Bugs",
        "Unreal Game Bugs",
        "Mobile Game Bugs",
        "WebGL Game Bugs",
        "Multiplayer Bugs",
        "Wallet Bugs",
        "Admin Panel Bugs",
        "Tournament Bugs",
        "Leaderboard Bugs",
        "LiveOps Bugs",
        "Crash Fixing",
        "Regression Testing"
    ];

    const faqs = [
        {
            question: "What type of game bugs can Oengines fix?",
            answer: "We can fix gameplay, multiplayer, wallet, reward, UI, LiveOps, tournament, leaderboard, admin panel, build, device, and release-related game bugs."
        },
        {
            question: "Can you fix bugs in an existing game project?",
            answer: "Yes. We can review existing Unity, Unreal, mobile, web, backend, admin panel, and game server codebases and help fix reported issues."
        },
        {
            question: "Do you verify bugs after fixing?",
            answer: "Yes. We retest the reported issue, run regression checks around related flows, and provide verification notes for release confidence."
        },
        {
            question: "Can you fix wallet and real-money game issues?",
            answer: "Yes. We can investigate wallet updates, transaction states, reward claims, tournament prizes, refunds, bonus logic, and economy-related bugs."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Game Bug Fixing Services",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Bug Fixing",
        "areaServed": "Worldwide",
        "description": "Game bug fixing services for gameplay, multiplayer, wallet, rewards, UI, LiveOps, tournaments, leaderboards, admin panels, crashes, regression testing, and release blockers."
    };

    return (
        <>
            <Helmet>
                <title>Game Bug Fixing Services | Gameplay, Multiplayer & Wallet Fixes | Oengines</title>
                <meta name="description" content="Oengines Games LLP provides game bug fixing services for gameplay, multiplayer, wallet, rewards, UI, LiveOps, tournaments, leaderboards, crashes, admin panels, and release blockers." />
                <meta name="keywords" content="game bug fixing, game bug fixing services, Unity bug fixing, multiplayer bug fixing, gameplay bug fixing, wallet bug fixing, game crash fixing, game QA support" />
                <link rel="canonical" href="https://www.oengines.com/game-bug-fixing" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="bugfix-page">
                <section className="bugfix-hero">
                    <div className="container">
                        <div className="bugfix-hero-grid">
                            <div>
                                <span className="bugfix-eyebrow">Game Bug Fixing</span>
                                <h1>Fix Gameplay, Multiplayer, Wallet & Release Bugs Faster</h1>
                                <p>Oengines Games LLP helps game studios and product teams investigate, fix, verify, and prevent bugs across mobile games, web games, multiplayer platforms, admin panels, LiveOps tools, and game backend systems.</p>
                                <div className="bugfix-actions">
                                    <a href="/contact" className="as-btn">Fix My Game Bugs <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="bugfix-call">Call: (0261) - 4054978</a>
                                </div>
                            </div>
                            <div className="bugfix-board">
                                <div className="bugfix-board-head">
                                    <span></span><span></span><span></span>
                                    <strong>Bug Triage Board</strong>
                                </div>
                                <div className="bugfix-lanes">
                                    {lanes.map((lane) => (
                                        <article key={lane.title}>
                                            <div>
                                                <h3>{lane.title}</h3>
                                                <strong>{lane.count}</strong>
                                            </div>
                                            {lane.items.map((item) => <span key={item}>{item}</span>)}
                                        </article>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bugfix-type-section space">
                    <div className="container">
                        <div className="bugfix-heading text-center">
                            <span>Bug Fixing Coverage</span>
                            <h2>Game Bugs We Can Investigate & Fix</h2>
                            <p>We focus on bugs that affect player trust, monetization, live operations, retention, real-time gameplay, and release quality.</p>
                        </div>
                        <div className="bugfix-type-grid">
                            {bugTypes.map((item) => (
                                <article key={item.title}>
                                    <i className={item.icon}></i>
                                    <h3>{item.title}</h3>
                                    <p>{item.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="bugfix-triage-section space">
                    <div className="container">
                        <div className="bugfix-split-heading">
                            <div className="bugfix-heading mb-0">
                                <span>Severity Triage</span>
                                <h2>Prioritize the Bugs That Block Players, Revenue & Releases</h2>
                            </div>
                            <p>Bug fixing works best when every issue has reproducible steps, severity, impacted area, owner, logs, screenshots, and verification status.</p>
                        </div>
                        <div className="bugfix-table-wrap">
                            <table className="bugfix-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Issue</th>
                                        <th>Area</th>
                                        <th>Severity</th>
                                        <th>Owner</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {triage.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.id}</td>
                                            <td>{item.issue}</td>
                                            <td>{item.area}</td>
                                            <td><span>{item.severity}</span></td>
                                            <td>{item.owner}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>

                <section className="bugfix-debug-section space">
                    <div className="container">
                        <div className="row align-items-center g-5">
                            <div className="col-lg-5">
                                <div className="bugfix-heading">
                                    <span>Debug Review</span>
                                    <h2>Trace the Real Cause, Not Only the Visible Error</h2>
                                    <p>Game bugs often come from state mismatch, backend timing, wallet rules, network delay, device differences, or incomplete UI handling. We review the flow behind the issue before fixing it.</p>
                                </div>
                                <div className="bugfix-chip-list">
                                    {coverage.map((item) => <span key={item}>{item}</span>)}
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="bugfix-debug-panel">
                                    <img src="assets/img/project/Faster_fixes.jpg" alt="Game bug fixing" />
                                    <div className="bugfix-log-panel">
                                        <span>debug-log</span>
                                        <code>match_state: reconnect_pending</code>
                                        <code>wallet_delta: duplicate_reward</code>
                                        <code>leaderboard_sync: delayed</code>
                                        <code>fix_status: verified</code>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bugfix-flow-section space">
                    <div className="container">
                        <div className="bugfix-heading text-center">
                            <span>Fix Workflow</span>
                            <h2>From Reproduction to Regression Verification</h2>
                        </div>
                        <div className="bugfix-flow-grid">
                            {fixFlow.map((item) => (
                                <article key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="bugfix-regression-section space">
                    <div className="container">
                        <div className="bugfix-regression-panel">
                            <div className="bugfix-heading mb-0">
                                <span>Regression Safety</span>
                                <h2>Fix Bugs Without Breaking Related Game Flows</h2>
                                <p>After fixing a bug, we retest connected systems such as wallet updates, game results, leaderboards, reconnects, rewards, admin reports, and release smoke flows.</p>
                            </div>
                            <div className="bugfix-regression-grid">
                                <div><strong>Original Issue</strong><span>Re-tested with exact steps</span></div>
                                <div><strong>Adjacent Flow</strong><span>Checked around impacted feature</span></div>
                                <div><strong>Release Risk</strong><span>Validated before build handoff</span></div>
                                <div><strong>QA Notes</strong><span>Documented for future regression</span></div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bugfix-faq-section space">
                    <div className="container">
                        <div className="bugfix-heading text-center">
                            <span>FAQ</span>
                            <h2>Game Bug Fixing Questions</h2>
                        </div>
                        <div className="bugfix-faq-grid">
                            {faqs.map((item) => (
                                <article key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="bugfix-cta">
                    <div className="container">
                        <div className="bugfix-cta-box">
                            <div>
                                <span>Bug Fixing Consultation</span>
                                <h2>Have critical bugs blocking your game release?</h2>
                                <p>Share your build, bug list, device details, videos, logs, backend notes, and release deadline. We will help review and plan the fixes.</p>
                            </div>
                            <a href="/contact" className="as-btn">Start Bug Fixing <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
